import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Globe2, AlertTriangle, Coins } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { apiGet, formatLKR, formatDate } from "@/lib/api";

/**
 * Shown on the Stock Detail Overview tab.
 * Pulls:
 *   - Recent foreign flow rows for this symbol
 *   - Upcoming dividend (if any)
 */
export const MarketContextCard = ({ cseSymbol }) => {
  const [flows, setFlows] = useState([]);
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!cseSymbol) return;
    let active = true;
    Promise.all([
      apiGet(`/market-intelligence/foreign-flows/${encodeURIComponent(cseSymbol)}?days=14`).catch(() => []),
      apiGet("/market-intelligence/latest").catch(() => null),
    ]).then(([f, latestData]) => {
      if (!active) return;
      setFlows(Array.isArray(f) ? f : []);
      setLatest(latestData);
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [cseSymbol]);

  if (loading) return null;

  const lastFlow = flows[0];
  const upcoming = (latest?.upcoming_dividends || []).find((d) => d.cse_symbol === cseSymbol);

  if (!lastFlow && !upcoming) {
    // Render a small empty state rather than nothing
    return (
      <Card data-testid="market-context-card" className="bg-muted/20">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Globe2 className="h-4 w-4 text-muted-foreground" /> Market Context
          </CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-muted-foreground">
          No recent foreign flow or dividend announcement for {cseSymbol}.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="market-context-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Globe2 className="h-4 w-4 text-primary" /> Market Context
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {lastFlow && (
          <div className="flex items-start gap-2" data-testid="market-context-foreign-flow">
            {lastFlow.direction === "buy" ? (
              <Globe2 className="h-4 w-4 mt-0.5 text-[hsl(var(--success))]" />
            ) : (
              <AlertTriangle className="h-4 w-4 mt-0.5 text-[hsl(var(--destructive))]" />
            )}
            <div>
              <div className="font-medium">
                Net foreign {lastFlow.direction === "buy" ? "buying" : "selling"}{" "}
                <span className={lastFlow.direction === "buy" ? "text-[hsl(var(--success))]" : "text-[hsl(var(--destructive))]"}>
                  {formatLKR((lastFlow.amount_lkr_mn || 0) * 1e6, { compact: true })}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">
                On {formatDate(lastFlow.date)} —{" "}
                {lastFlow.direction === "buy"
                  ? "positive institutional interest"
                  : "foreign investors reducing exposure"}
              </div>
            </div>
          </div>
        )}
        {upcoming && (
          <div className="flex items-start gap-2" data-testid="market-context-dividend">
            <Coins className="h-4 w-4 mt-0.5 text-amber-500" />
            <div className="flex-1">
              <div className="font-medium">
                Upcoming dividend — Rs. {Number(upcoming.dps_gross || upcoming.dividend_per_share || 0).toFixed(2)} gross
              </div>
              <div className="text-xs text-muted-foreground">
                You receive Rs. {Number(upcoming.dps_net ?? ((upcoming.dps_gross || upcoming.dividend_per_share || 0) * 0.9)).toFixed(2)} net (after 10% WHT)
              </div>
              <div className="flex items-center gap-2 mt-1.5">
                {upcoming.ex_dividend_date && (
                  <Badge variant="outline" className="text-[10px]">
                    XD: {formatDate(upcoming.ex_dividend_date)}
                  </Badge>
                )}
                {upcoming.payment_date && (
                  <Badge variant="outline" className="text-[10px]">
                    Pay: {formatDate(upcoming.payment_date)}
                  </Badge>
                )}
              </div>
              {upcoming.ex_dividend_date && (
                <div className="text-[11px] text-muted-foreground mt-1">
                  Buy before {formatDate(upcoming.ex_dividend_date)} to qualify.
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MarketContextCard;
